import { css, cx } from "@emotion/css";
import { BanIcon, CheckCircleIcon, CheckWarningCircleIcon, DashedCircleIcon, QuestionCircleIcon, XmarkCircleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import React from "react";
export const badgeStatuses = ["healthy", "healthy-with-warnings", "unavailable", "unhealthy", "unknown", "disabled"] as const;
export type BadgeStatus = (typeof badgeStatuses)[number];
export interface StatusBadgeProps {
    /**
     * The status to display.
     * Determines the icon, label, and color scheme of the badge.
     */
    status: BadgeStatus;
    /**
     * Whether to show the text label alongside the icon.
     * @default false
     */
    showLabel?: boolean;
}
export function StatusBadge({ status, showLabel = false }: StatusBadgeProps) {
    const { icon: StatusIcon, label } = getIconAndLabel(status);
    return (<div className={cx(badgeStyles, statusStyles[status])}>
            <StatusIcon size={20} title={!showLabel ? label : undefined}/>
            {showLabel && label}
        </div>);
}
function getIconAndLabel(status: BadgeStatus) {
    switch (status) {
        case "healthy":
            return { icon: CheckCircleIcon, label: "Healthy" };
        case "healthy-with-warnings":
            return { icon: CheckWarningCircleIcon, label: "Healthy with warnings" };
        case "unavailable":
            return { icon: BanIcon, label: "Unavailable" };
        case "unhealthy":
            return { icon: XmarkCircleIcon, label: "Unhealthy" };
        case "unknown":
            return { icon: QuestionCircleIcon, label: "Unknown" };
        case "disabled":
            return { icon: DashedCircleIcon, label: "Disabled" };
        default:
            return exhaustiveCheck(status, `Unexpected status ${status} for StatusBadge`);
    }
}
const badgeStyles = css({
    boxSizing: "border-box",
    display: "inline-flex",
    borderRadius: borderRadius.circle,
    alignItems: "center",
    justifyContent: "center",
    padding: `${space[4]} ${space[8]}`,
    gap: space[4],
    font: text.body.regular.medium,
});
const statusStyles: Record<BadgeStatus, string> = {
    healthy: css({
        backgroundColor: themeTokens.color.statusBadge.background.healthy,
        color: themeTokens.color.statusBadge.text.primary,
        svg: {
            color: themeTokens.color.statusBadge.icon.default,
        },
    }),
    "healthy-with-warnings": css({
        backgroundColor: themeTokens.color.statusBadge.background.healthy,
        color: themeTokens.color.statusBadge.text.primary,
        svg: {
            color: themeTokens.color.statusBadge.icon.default,
        },
    }),
    unavailable: css({
        backgroundColor: themeTokens.color.statusBadge.background.warning,
        color: themeTokens.color.statusBadge.text.primary,
        svg: {
            color: themeTokens.color.statusBadge.icon.default,
        },
    }),
    unhealthy: css({
        backgroundColor: themeTokens.color.statusBadge.background.unhealthy,
        color: themeTokens.color.statusBadge.text.primary,
        svg: {
            color: themeTokens.color.statusBadge.icon.default,
        },
    }),
    unknown: css({
        backgroundColor: themeTokens.color.statusBadge.background.neutral,
        color: themeTokens.color.statusBadge.text.inverse,
        svg: {
            color: themeTokens.color.statusBadge.icon.inverse,
        },
    }),
    disabled: css({
        backgroundColor: themeTokens.color.statusBadge.background.neutral,
        color: themeTokens.color.statusBadge.text.inverse,
        svg: {
            color: themeTokens.color.statusBadge.icon.inverse,
        },
    }),
};
